'use client'

import { useState } from 'react'
import { nav } from '@/lib/marketing/content'
import { Hero } from '@/components/marketing/sections/Hero'
import { TrustBar } from '@/components/marketing/sections/TrustBar'
import { WhySwitch } from '@/components/marketing/sections/WhySwitch'
import { OperationsModules } from '@/components/marketing/sections/OperationsModules'
import { LessonsSpotlight } from '@/components/marketing/sections/LessonsSpotlight'
import { BrandSection } from '@/components/marketing/sections/BrandSection'
import { PricingTeaser } from '@/components/marketing/sections/PricingTeaser'
import { FinalCTA } from '@/components/marketing/sections/FinalCTA'
import { FAQ } from '@/components/marketing/sections/FAQ'
import { Testimonial } from '@/components/marketing/sections/Testimonial'
import { CompetitorComparison } from '@/components/marketing/sections/CompetitorComparison'
import { Footer } from '@/components/marketing/sections/Footer'
import { DemoRequestModal } from '@/components/marketing/DemoRequestModal'

interface LandingPageContentProps {
  logoUrl?: string | null
  platformName?: string
}

export function LandingPageContent({ logoUrl, platformName = 'Ordo Golf' }: LandingPageContentProps) {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false)

  const openDemoModal = () => setIsDemoModalOpen(true)
  const closeDemoModal = () => setIsDemoModalOpen(false)

  return (
    <div className="relative min-h-screen bg-[#002C2B] text-white overflow-x-hidden">
      {/* Golf background image */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1555983341-fe83dada1a2b?q=80&w=1974&auto=format&fit=crop)' }}
      />
      
      {/* Noise texture overlay */}
      <div className="noise-bg" />

      {/* Subtle gradient orbs */}
      <div className="fixed top-0 left-1/4 w-[700px] h-[700px] bg-[#71ED7F]/[0.02] rounded-full blur-[160px] pointer-events-none" />
      <div className="fixed bottom-0 right-0 w-[500px] h-[500px] bg-[#E4FFE7]/[0.015] rounded-full blur-[140px] pointer-events-none" />

      {/* Nav - Dark Pine Glass */}
      <nav className="fixed top-0 left-0 right-0 z-40">
        <div className="mx-4 mt-4">
          <div className="max-w-[960px] mx-auto bg-[#001716]/80 backdrop-blur-2xl rounded-2xl px-6 h-16 flex items-center justify-between border border-[#E4FFE7]/[0.06] shadow-lg shadow-black/20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2.5 group">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={platformName}
                  className="h-8 w-auto transition-all duration-300 group-hover:scale-105"
                />
              ) : (
                <>
                  <div className="w-8 h-8 rounded-xl bg-[#71ED7F] flex items-center justify-center group-hover:shadow-[0_0_20px_-5px_rgba(113,237,127,0.5)] transition-all">
                    <span className="text-[#001716] font-bold text-sm">O</span>
                  </div>
                  <span className="font-semibold text-base text-white/90">{platformName}</span>
                </>
              )}
            </a>

            {/* CTA */}
            <div className="flex items-center gap-4">
              <button
                onClick={openDemoModal}
                className="bg-[#71ED7F] text-[#001716] px-5 py-2 rounded-full text-sm font-semibold hover:shadow-[0_0_30px_-5px_rgba(113,237,127,0.5)] transition-all"
              >
                {nav.cta}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main>
        <Hero onOpenDemo={openDemoModal} />
        <TrustBar />
        <WhySwitch />
        <OperationsModules />
        <LessonsSpotlight />
        <BrandSection />
        <PricingTeaser onOpenDemo={openDemoModal} />
        <CompetitorComparison />
        <Testimonial />
        <FAQ />
        <FinalCTA onOpenDemo={openDemoModal} />
      </main>

      <Footer logoUrl={logoUrl} platformName={platformName} />

      {/* Demo Modal */}
      <DemoRequestModal isOpen={isDemoModalOpen} onClose={closeDemoModal} />
    </div>
  )
}
